import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { AlertCircle, Calendar, CalendarDays, Check, CheckCircle, ChevronLeftIcon, Circle, Clock, Download, Edit, Eye, FileText, Filter, IndianRupee, Minus, MinusCircle, PieChart, Play, PlusCircle, RefreshCw, Send, TrendingUp, UserCheck, Users, Zap, X, Stethoscope } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_PAYSLIP_EDIT, PAYROLL_PROCESS, PAYROLL_PUBLISH, PAYROLL_BANK_ADVICE, PAYROLL_RECALCULATE, PAYROLL_SHORT_DUTY_REVIEW, PAYROLL_ATTENDANCE_PENALTIES, INTEGRATION_DOCTOR_SHARE_DETAIL, PAYROLL_ADVANCE_EMI_SKIP } from "../../constants/api";
import { Reactable } from "../../components/ui/reactable";
import ToastMsg from "../../components/common/ToastMsg";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { getCSVData, checkUserPermissionFunc, formatPayrollCurrency } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import PayrollProfileLockBanner from "./PayrollProfileLockBanner";

const { Option } = Select;

/**
 * Overtime cell — shows total OT hours + amount, with a click-open mini calendar
 * highlighting the days overtime was applied (hours per day on hover). The panel
 * is fixed-positioned so it escapes the table's horizontal overflow clipping.
 */
const OvertimeCell = ({ data, periodFrom }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);

    const amount = Number(data?.amount || 0);
    const totalHours = Number(data?.total_hours || 0);
    const dates = Array.isArray(data?.dates) ? data.dates : [];

    if (amount <= 0 && totalHours <= 0) {
        return <span className="text-gray-300 dark:text-gray-600">—</span>;
    }

    const displayAmount = amount > 0 ? amount : 0;

    const toggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({
                top: r.bottom + 6,
                left: Math.max(8, Math.min(r.left, window.innerWidth - 268)),
            });
        }
        setOpen((o) => !o);
    };

    // Month to render: first OT date, else the payroll period start.
    const anchor = dates.length ? moment(dates[0].date) : (periodFrom ? moment(periodFrom) : moment());
    const daysInMonth = anchor.daysInMonth();
    const leadingBlanks = anchor.clone().startOf("month").day(); // 0=Sun
    const hoursByDay = {};
    dates.forEach((d) => {
        const m = moment(d.date);
        if (m.month() === anchor.month() && m.year() === anchor.year()) {
            hoursByDay[m.date()] = (hoursByDay[m.date()] || 0) + Number(d.hours || 0);
        }
    });
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={toggle}
                title="View overtime days"
                className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
            >
                <Clock className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{totalHours}h</span>
                {displayAmount > 0 ? (
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        ₹{displayAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                ) : (
                    <span className="text-xs text-amber-600 dark:text-amber-400">₹0.00</span>
                )}
            </button>

            {open && coords && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div
                        className="fixed z-50 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-3"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                {anchor.format("MMMM YYYY")}
                            </span>
                            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-7 gap-0.5 mb-1 text-[10px] text-center text-gray-400 dark:text-gray-500">
                            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                                <div key={i}>{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-0.5">
                            {cells.map((d, i) =>
                                d === null ? (
                                    <div key={i} />
                                ) : (
                                    <div
                                        key={i}
                                        title={hoursByDay[d] ? `${hoursByDay[d]}h overtime` : ""}
                                        className={cn(
                                            "h-7 rounded-md text-[11px] flex items-center justify-center",
                                            hoursByDay[d]
                                                ? "bg-emerald-500 text-white font-semibold cursor-default"
                                                : "text-gray-500 dark:text-gray-400"
                                        )}
                                    >
                                        {d}
                                    </div>
                                )
                            )}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">
                                Total <span className="font-semibold text-gray-800 dark:text-gray-200">{totalHours}h</span>
                            </span>
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                ₹{amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const ADJUST_THEME = {
    emerald: { dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", add: "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400", focus: "focus:border-emerald-400 focus:ring-emerald-400/25" },
    rose: { dot: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", add: "text-rose-600 hover:text-rose-700 dark:text-rose-400", focus: "focus:border-rose-400 focus:ring-rose-400/25" },
    indigo: { dot: "bg-indigo-500", text: "text-indigo-600 dark:text-indigo-400", add: "text-indigo-600 hover:text-indigo-700 dark:text-indigo-400", focus: "focus:border-indigo-400 focus:ring-indigo-400/25" },
};

/** Badge for allowance/deduction lines adjusted during payroll review. */
const OverrideBadge = ({ item }) => {
    if (!item?.overridden) return null;
    const original = item.original_amount;
    const assignmentAmount = item.assignment_amount;
    const title = item.out_of_sync
        ? `Assignment is ₹${Number(assignmentAmount ?? original ?? 0).toLocaleString('en-IN')}; payslip shows ₹${Number(item.amount || 0).toLocaleString('en-IN')}. Save payroll to sync.`
        : original != null
            ? `Originally ₹${Number(original).toLocaleString('en-IN')} — synced to Employee Deductions/Allowances`
            : 'Adjusted during payroll review';
    return (
        <span
            title={title}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border border-amber-200 dark:border-amber-700 shrink-0"
        >
            {item.out_of_sync ? 'Out of sync' : 'Override'}
        </span>
    );
};

/** Compact editable list of {name, amount} rows (Credits / Debits / Reimbursements). */
const AdjustmentGroup = ({ title, accent = "emerald", items = [], total, namePlaceholder, onAdd, onChange, onRemove, readOnlyPredicate }) => {
    const theme = ADJUST_THEME[accent] || ADJUST_THEME.emerald;
    const inputCls = cn(
        "min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition",
        theme.focus
    );
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 rounded-full", theme.dot)} />
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
                </div>
                <button type="button" onClick={onAdd} className={cn("inline-flex items-center gap-1 text-xs font-medium transition-colors", theme.add)}>
                    <PlusCircle className="h-3.5 w-3.5" /> Add
                </button>
            </div>
            <div className="space-y-2">
                {items?.map((item, index) => {
                    const readOnly = readOnlyPredicate?.(item, index);
                    return (
                    <div key={index} className="flex items-start gap-2">
                        <div className="flex-1 min-w-0 space-y-1">
                        <input
                            placeholder={namePlaceholder}
                            value={item.name}
                            onChange={(e) => onChange(index, "name", e.target.value)}
                                className={cn(inputCls, "w-full", readOnly && "bg-gray-50 dark:bg-gray-800/80 cursor-not-allowed")}
                            readOnly={readOnly}
                            disabled={readOnly}
                        />
                            <OverrideBadge item={item} />
                        </div>
                        <div className="relative w-24 flex-shrink-0">
                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                            <input
                                type="number"
                                placeholder="0"
                                value={item.amount}
                                onChange={(e) => onChange(index, "amount", e.target.value)}
                                className={cn(inputCls, "w-full pl-5 text-right", readOnly && "bg-gray-50 dark:bg-gray-800/80 cursor-not-allowed")}
                                readOnly={readOnly}
                                disabled={readOnly}
                            />
                        </div>
                        {items.length > 1 && !readOnly ? (
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                className="flex-shrink-0 grid place-items-center h-7 w-7 rounded-md text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        ) : (
                            <span className="w-7 flex-shrink-0" />
                        )}
                    </div>
                );})}
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                <span className="text-xs text-gray-500 dark:text-gray-400">Subtotal</span>
                <span className={cn("text-sm font-semibold", theme.text)}>₹{Number(total || 0).toLocaleString("en-IN")}</span>
            </div>
        </div>
    );
};

/**
 * Real deduction lines out of a payslip's deductions_breakup.
 *
 * Two quirks of the stored data this handles:
 *
 *  1. Placeholder entries. Some payslips carry an entry for every deduction
 *     component ever configured on the company, with every field null. Those are
 *     not deductions and must not be listed. (The Earnings panel already skips
 *     its equivalents via `earning?.name && earning.monthly_amount !== null`.)
 *     A genuine zero — PF/ESIC during full LOP — has 0, not null, so it stays.
 *
 *  2. The dict KEY is the reliable label. On those legacy entries the inner
 *     `name` is null while the key still holds the component name, so rendering
 *     `deduction.name` alone produced blank rows.
 */
const visibleDeductions = (breakup) =>
    Object.entries(breakup || {})
        .filter(([, d]) => d && d.employee_share_monthly_amount !== null && d.employee_share_monthly_amount !== undefined)
        .map(([key, d]) => ({ ...d, label: d.name || key }));

const sumEarningsBreakup = (breakup) =>
    Object.entries(breakup || {})
        .filter(([key]) => !key.startsWith('_'))
        .reduce((sum, [, item]) => sum + Number(item?.monthly_amount || 0), 0);

const getTdsAmount = (row) => {
    const breakup = row?.deductions_breakup || {};
    const entry = breakup.TDS || breakup.tds;
    if (!entry) return 0;
    return Number(entry.employee_share_monthly_amount ?? entry.amount ?? 0);
};

/** Mirrors backend paid_salary: net already includes structure deductions. */
const computeUpdatedFinalPay = (row, totalCredit = 0, totalReimburse = 0, totalDebit = 0) => {
    const netPay = Number(row?.net_pay || 0);
    const lop = Number(row?.lop_amount || 0);
    const credit = Number(totalCredit || 0);
    const reimburse = Number(totalReimburse || 0);
    const debit = Number(totalDebit || 0);
    const tds = getTdsAmount(row);
    const amount = netPay - lop + credit + reimburse - debit - tds;
    return Math.max(0, Math.round(amount * 100) / 100);
};

const displayEarningsTotal = (row) => {
    const fromBreakup = sumEarningsBreakup(row?.earnings_breakup);
    if (fromBreakup > 0) return fromBreakup;
    return Number(row?.earnings || 0);
};

const mapBreakupWithMeta = (breakup, meta) => {
    const entries = Object.entries(breakup || {});
    if (entries.length === 0) {
        return [{ name: "", amount: 0 }];
    }
    return entries.map(([name, amount]) => ({
        name,
        amount: typeof amount === 'object' && amount !== null
            ? Number(amount.amount ?? amount.employee_share_monthly_amount ?? amount.monthly_amount ?? 0)
            : Number(amount || 0),
        overridden: Boolean(meta?.[name]?.overridden),
        original_amount: meta?.[name]?.original_amount,
        out_of_sync: Boolean(meta?.[name]?.out_of_sync),
        assignment_amount: meta?.[name]?.assignment_amount,
    }));
};

const SHORT_DUTY_FINE_LABEL = 'Short Duty Fine';
const OVERTIME_CREDIT_LABEL = 'Overtime';

const isOvertimeCreditName = (name) =>
    (name || '').trim().toLowerCase() === OVERTIME_CREDIT_LABEL.toLowerCase();

const splitOvertimeFromCredits = (credits = []) => {
    let overtimeAmount = 0;
    const rest = [];
    credits.forEach((item) => {
        if (isOvertimeCreditName(item.name)) {
            overtimeAmount = Number(item.amount || 0);
        } else {
            rest.push(item);
        }
    });
    return { rest, overtimeAmount };
};

const displayPaidSalary = (value) =>
    Math.max(0, Math.round(Number(value || 0) * 100) / 100);

const getShortDutyFineFromBreakup = (breakup) => {
    if (!breakup || typeof breakup !== 'object') return 0;
    for (const [name, amount] of Object.entries(breakup)) {
        if ((name || '').trim().toLowerCase() === SHORT_DUTY_FINE_LABEL.toLowerCase()) {
            return Number(amount || 0);
        }
    }
    return 0;
};

const payslipShortDutyFineSnapshot = (row) => Math.max(
    Number(row?.short_duty_fine || 0),
    getShortDutyFineFromBreakup(row?.debit_breakup),
);

const ensureShortDutyFineInDebits = (debits, shortDutyFine) => {
    const fineAmount = Number(shortDutyFine || 0);
    if (fineAmount <= 0) {
        return debits.filter(
            (item) => (item.name || '').trim().toLowerCase() !== SHORT_DUTY_FINE_LABEL.toLowerCase(),
        );
    }
    const list = debits.filter(
        (item) => (item.name || '').trim().toLowerCase() !== SHORT_DUTY_FINE_LABEL.toLowerCase(),
    );
    const fineEntry = {
        name: SHORT_DUTY_FINE_LABEL,
        amount: fineAmount,
        isShortDutyFine: true,
    };
    const emptyIdx = list.findIndex((item) => !(item.name || '').trim() && !Number(item.amount));
    if (emptyIdx >= 0) {
        list[emptyIdx] = fineEntry;
    } else {
        list.unshift(fineEntry);
    }
    return list;
};

const RunPayrollProcess = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const { fetch, create, update } = useApiMethods()
    const { fromEdit, PaySlipId, editDetails, isActive } = location.state || {};
    // Payroll mutations (edit payslip, publish, finalize) require edit permission.
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");
    const [loading, setLoading] = useState(false);
    const [individualLoading, setIndividualLoading] = useState({});
    const [runPayrollLoading, setRunPayrollLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [hasCompletedAnimation, setHasCompletedAnimation] = useState(() => {
        // Check sessionStorage on initial load to persist state across refreshes
        return sessionStorage.getItem("hasCompletedAnimation") === "true";
    });
    const [payrollData, setPayrollData] = useState(null);
    const [everythingPublished, setEverythingPublished] = useState(false);
    const [RunPayrollProcessData, setRunPayrollProcessData] = useState([])
    const [payrollTableTrigger, setPayrollTableTrigger] = useState(false)
    const [, setPayrollTableTotal] = useState(0)

    const [openDialog, setOpenDialog] = useState(false)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [recalculateConfirmOpen, setRecalculateConfirmOpen] = useState(false)
    const [recalculateLoading, setRecalculateLoading] = useState(false)
    const [shortDutyDialogOpen, setShortDutyDialogOpen] = useState(false)
    const [shortDutyItems, setShortDutyItems] = useState([])
    const [pendingPenaltyTotal, setPendingPenaltyTotal] = useState(0)
    const [pendingPenaltyPeriodLabel, setPendingPenaltyPeriodLabel] = useState("")
    const [pendingPenaltyFromDate, setPendingPenaltyFromDate] = useState("")
    const [pendingPenaltyToDate, setPendingPenaltyToDate] = useState("")
    const [shortDutyCheckLoading, setShortDutyCheckLoading] = useState(false)
    const [penaltyAcceptLoadingId, setPenaltyAcceptLoadingId] = useState(null)
    const [penaltyBulkAcceptLoading, setPenaltyBulkAcceptLoading] = useState(false)
    const [penaltyEditOpen, setPenaltyEditOpen] = useState(false)
    const [penaltyEditRow, setPenaltyEditRow] = useState(null)
    const [penaltyFormData, setPenaltyFormData] = useState({ mode: "deduction", deduction: 0.5, fine: 0 })
    const [penaltySaveLoading, setPenaltySaveLoading] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    // Add these state variables
    const [debitList, setDebitList] = useState([{ name: "", amount: 0 }]);
    const [totalDebit, setTotalDebit] = useState(0);
    const [creditList, setCreditList] = useState([{ name: "", amount: 0 }]);
    const [totalCredit, setTotalCredit] = useState(0);
    const [reimburseList, setReimburseList] = useState([{ name: "", amount: 0 }]);
    const [totalReimburse, setTotalReimburse] = useState(0);
    const [advanceEmiSkipLoading, setAdvanceEmiSkipLoading] = useState({});
    const [advanceEmiCarryMonth, setAdvanceEmiCarryMonth] = useState({});
    const [advanceEmiSkipReason, setAdvanceEmiSkipReason] = useState({});
    const [liveShortDutyFine, setLiveShortDutyFine] = useState(0);
    const [liveShortDutyFineDays, setLiveShortDutyFineDays] = useState(0);
    const [overtimeAmount, setOvertimeAmount] = useState(0);
    const [overtimeHours, setOvertimeHours] = useState(0);
    const [attentionPage, setAttentionPage] = useState(0);

    const payrollMonthBase = payrollData?.payroll_year && payrollData?.payroll_month
        ? dayjs(`${payrollData.payroll_year}-${String(payrollData.payroll_month).padStart(2, "0")}-01`)
        : payrollData?.from_date
            ? dayjs(payrollData.from_date).startOf("month")
            : null;

    const payrollMonthLabel = payrollMonthBase
        ? payrollMonthBase.format("MMMM YYYY")
        : "-";

    const applyPayslipDebitFromResponse = (payslipPayload) => {
        const debitMeta = selectedRow?.debit_breakup_meta || {};
        const breakup = payslipPayload?.debit_breakup || {};
        const initialDebits = Object.keys(breakup).length
            ? mapBreakupWithMeta(breakup, debitMeta)
            : [{ name: "", amount: 0 }];
        setDebitList(initialDebits);
        setSelectedRow((prev) => prev ? {
            ...prev,
            debit: payslipPayload?.debit ?? prev.debit,
            debit_breakup: breakup,
            paid_salary: payslipPayload?.paid_salary ?? prev.paid_salary,
        } : prev);
    };

    const refreshAdvanceEmiOnRow = (items, payslipPayload) => {
        setSelectedRow((prev) => prev ? { ...prev, advance_emi_items: items || [] } : prev);
        if (payslipPayload) {
            applyPayslipDebitFromResponse(payslipPayload);
        }
        setRunPayrollProcessData((prev) => prev.map((row) => (
            row.payslip_id === editId
                ? {
                    ...row,
                    advance_emi_items: items || row.advance_emi_items,
                    debit: payslipPayload?.debit ?? row.debit,
                    debit_breakup: payslipPayload?.debit_breakup ?? row.debit_breakup,
                    paid_salary: payslipPayload?.paid_salary ?? row.paid_salary,
                }
                : row
        )));
    };

    const handleAdvanceEmiSkip = async (item) => {
        const carryMonth = advanceEmiCarryMonth[item.advance_salary_id];
        if (!carryMonth) {
            ToastMsg("error", "Select a carry-forward month");
            return;
        }
        setAdvanceEmiSkipLoading((prev) => ({ ...prev, [item.advance_salary_id]: true }));
        try {
            const res = await create(PAYROLL_ADVANCE_EMI_SKIP, {
                payslip_id: editId,
                advance_salary_id: item.advance_salary_id,
                action: "skip",
                carry_to_month: `${carryMonth}-01`,
                skip_reason: advanceEmiSkipReason[item.advance_salary_id] || "",
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Advance EMI skipped");
                refreshAdvanceEmiOnRow(res.data?.items, res.data?.payslip);
            } else {
                ToastMsg("error", res?.message || "Failed to skip advance EMI");
            }
        } catch (error) {
            ToastMsg("error", "Failed to skip advance EMI");
        } finally {
            setAdvanceEmiSkipLoading((prev) => ({ ...prev, [item.advance_salary_id]: false }));
        }
    };

    const handleAdvanceEmiRestore = async (item) => {
        setAdvanceEmiSkipLoading((prev) => ({ ...prev, [item.advance_salary_id]: true }));
        try {
            const res = await create(PAYROLL_ADVANCE_EMI_SKIP, {
                payslip_id: editId,
                advance_salary_id: item.advance_salary_id,
                action: "restore",
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Advance EMI restored");
                refreshAdvanceEmiOnRow(res.data?.items, res.data?.payslip);
            } else {
                ToastMsg("error", res?.message || "Failed to restore advance EMI");
            }
        } catch (error) {
            ToastMsg("error", "Failed to restore advance EMI");
        } finally {
            setAdvanceEmiSkipLoading((prev) => ({ ...prev, [item.advance_salary_id]: false }));
        }
    };

    const canManageAdvanceEmi = editPermission
        && payrollData?.status !== "completed"
        && !selectedRow?.is_publish;

    // eMedHub doctor-share "view details" modal
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareDetail, setShareDetail] = useState(null);
    const [shareEmployee, setShareEmployee] = useState(null);

    const openShareDetail = async (row) => {
        setShareEmployee(row);
        setShareDetail(null);
        setShareModalOpen(true);
        const res = await fetch(INTEGRATION_DOCTOR_SHARE_DETAIL, {
            employee_id: row.employee,
            from_date: payrollData?.from_date,
            to_date: payrollData?.to_date,
        }, setShareLoading);
        if (res?.status === "success") {
            setShareDetail(res.data || { data: [], Total_amount_card: "0.00", Doctor_share_card: "0.00" });
        } else {
            ToastMsg("error", res?.message || "Could not load doctor share details");
            setShareModalOpen(false);
        }
    };

    // Add these functions from your old code
    const calculateTotal = (list, setter) => {
        const total = list.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
        setter(total);
    };

    const handleListChange = (list, setter, index, statekey, value) => {
        let newList = [...list];
        if (statekey === "amount") {
            if (value === "" || value.length <= 7) {
                newList[index]["amount"] = +value;
            }
        } else {
            newList[index]["name"] = value.trim();
        }
        setter(newList);
    };

    const handleListAdd = (list, setter) => {
        const allPreviousLevelsFilled = list.every((item) => item.name && item.amount);
        if (allPreviousLevelsFilled) {
            const newItem = { name: "", amount: 0 };
            setter([...list, newItem]);
        } else {
            ToastMsg("error", "Please fill all previous levels before adding a new one.");
        }
    };

    const handleListRemove = (list, setter, index) => {
        if (list[index]?.isShortDutyFine) {
            ToastMsg("error", "Short-duty fine is applied from attendance review and cannot be removed here. Use Recalculate to refresh it.");
            return;
        }
        const newItems = [...list];
        newItems.splice(index, 1);
        setter(newItems);
    };

    const payslipSnapshotFine = openDialog && selectedRow
        ? payslipShortDutyFineSnapshot(selectedRow)
        : 0;
    const effectiveShortDutyFine = openDialog && selectedRow
        ? Math.max(payslipSnapshotFine, Number(liveShortDutyFine || 0))
        : 0;
    const effectiveShortDutyFineDays = openDialog && selectedRow
        ? Math.max(Number(selectedRow?.short_duty_fine_days || 0), Number(liveShortDutyFineDays || 0))
        : 0;
    const shortDutyFineStale = openDialog
        && liveShortDutyFine > payslipSnapshotFine + 0.001;

    // Pull reviewed short-duty fines from attendance when the edit dialog opens.
    useEffect(() => {
        if (!openDialog || !selectedRow || !payrollData?.from_date || !payrollData?.to_date) {
            setLiveShortDutyFine(0);
            setLiveShortDutyFineDays(0);
            return undefined;
        }
        let cancelled = false;
        const loadLiveFine = async () => {
            try {
                const res = await fetch(PAYROLL_SHORT_DUTY_REVIEW, {
                    from_date: payrollData.from_date,
                    to_date: payrollData.to_date,
                    reviewed: "true",
                }, false);
                if (cancelled || res?.status !== "success") return;
                const empId = selectedRow.employee;
                const fineItems = (res.data?.items || []).filter(
                    (it) => it.employee_id === empId && it.mode === "fine",
                );
                const total = fineItems.reduce((sum, it) => sum + Number(it.fine || 0), 0);
                setLiveShortDutyFine(Math.round(total * 100) / 100);
                setLiveShortDutyFineDays(fineItems.length);
            } catch {
                if (!cancelled) {
                    setLiveShortDutyFine(0);
                    setLiveShortDutyFineDays(0);
                }
            }
        };
        loadLiveFine();
        return () => { cancelled = true; };
    }, [openDialog, selectedRow, payrollData?.from_date, payrollData?.to_date, fetch]);

    // Add this useEffect after your state declarations
    useEffect(() => {
        if (openDialog && selectedRow) {
            // Initialize credits from selectedRow.credits_breakup
            const creditMeta = selectedRow.credits_breakup_meta || {};
            const initialCredits = selectedRow.credits_breakup && typeof selectedRow.credits_breakup === 'object'
                ? mapBreakupWithMeta(selectedRow.credits_breakup, creditMeta)
                : [{ name: "", amount: 0 }];

            // If credits exist but no breakup, create a default entry
            if (initialCredits.length === 0 && selectedRow.credits) {
                initialCredits.push({
                    name: "Additional Credit",
                    amount: selectedRow.credits
                });
            }
            const { rest: creditsWithoutOt, overtimeAmount: initialOvertimeAmount } = splitOvertimeFromCredits(initialCredits);
            setCreditList(creditsWithoutOt.length ? creditsWithoutOt : [{ name: "", amount: 0 }]);
            setOvertimeAmount(
                initialOvertimeAmount > 0
                    ? initialOvertimeAmount
                    : Number(selectedRow?.overtime?.amount || 0),
            );
            setOvertimeHours(Number(selectedRow?.overtime?.total_hours || 0));

            // Initialize debits from selectedRow.debit_breakup
            const debitMeta = selectedRow.debit_breakup_meta || {};
            const initialDebits = selectedRow.debit_breakup && typeof selectedRow.debit_breakup === 'object'
                ? mapBreakupWithMeta(selectedRow.debit_breakup, debitMeta)
                : [{ name: "", amount: 0 }];

            // If debit exists but no breakup, create a default entry
            if (initialDebits.length === 0 && selectedRow.debit) {
                initialDebits.push({
                    name: "Additional Debit",
                    amount: selectedRow.debit
                });
            }
            setDebitList(ensureShortDutyFineInDebits(initialDebits, effectiveShortDutyFine));

            // Initialize reimbursements from selectedRow.reimbursements (API field name)
            const reimburseSource = selectedRow.reimbursements ?? selectedRow.reimbursements_breakup;
            let initialReimburse = reimburseSource && typeof reimburseSource === 'object'
                ? Object.entries(reimburseSource)
                    .filter(([, amount]) => amount != null && amount !== '')
                    .map(([name, amount]) => ({
                        name,
                        amount: Number(amount) || 0,
                    }))
                : [];

            // If reimbursements_total exists but no breakup, create a default entry
            if (initialReimburse.length === 0 && selectedRow.reimbursements_total) {
                initialReimburse.push({
                    name: "Reimbursement",
                    amount: selectedRow.reimbursements_total
                });
            }
            if (initialReimburse.length === 0) {
                initialReimburse = [{ name: "", amount: 0 }];
            }
            setReimburseList(initialReimburse);

            console.log('Initialized lists:', {
                credits: creditsWithoutOt,
                overtimeAmount: initialOvertimeAmount,
                debits: initialDebits,
                reimbursements: initialReimburse
            });
        }
    }, [openDialog, selectedRow, effectiveShortDutyFine]);

    // Keep edit dialog in sync when payroll table refreshes (e.g. after Recalculate).
    useEffect(() => {
        if (!openDialog || !editId || !RunPayrollProcessData?.length) return;
        const fresh = RunPayrollProcessData.find((row) => row.payslip_id === editId);
        if (!fresh) return;
        const freshKey = JSON.stringify({
            short_duty_fine: fresh.short_duty_fine,
            short_duty_fine_days: fresh.short_duty_fine_days,
            debit_breakup: fresh.debit_breakup,
            paid_salary: fresh.paid_salary,
            lop_days: fresh.lop_days,
            lop_amount: fresh.lop_amount,
        });
        const currentKey = JSON.stringify({
            short_duty_fine: selectedRow?.short_duty_fine,
            short_duty_fine_days: selectedRow?.short_duty_fine_days,
            debit_breakup: selectedRow?.debit_breakup,
            paid_salary: selectedRow?.paid_salary,
            lop_days: selectedRow?.lop_days,
            lop_amount: selectedRow?.lop_amount,
        });
        if (freshKey !== currentKey) {
            setSelectedRow(fresh);
        }
    }, [RunPayrollProcessData, openDialog, editId, selectedRow]);

    // Add useEffect for calculations
    useEffect(() => {
        calculateTotal(debitList, setTotalDebit);
        calculateTotal(creditList, setTotalCredit);
        calculateTotal(reimburseList, setTotalReimburse);
    }, [debitList, creditList, reimburseList, calculateTotal]);

    const effectiveTotalCredit = useMemo(
        () => Math.round((Number(totalCredit || 0) + Number(overtimeAmount || 0)) * 100) / 100,
        [totalCredit, overtimeAmount],
    );

    // Published / draft counts come from payroll summary (not the paginated table page).
    const totalEmployees = Number(payrollData?.total_employee || 0)
    const publishedCount = Number(payrollData?.published_count ?? 0)
    const draftCount = Number(
        payrollData?.draft_count ?? Math.max(0, totalEmployees - publishedCount)
    )

    const getPayrollStatusLabel = (status) => ({
        yet_to_publish: 'Yet to Publish',
        published: 'Published',
        processing: 'Processing',
        pending: 'Pending',
        completed: 'Completed',
    }[status] || status || 'Unknown')

    const getPayrollStatusChipClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-700'
            case 'completed':
                return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700'
            case 'yet_to_publish':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
        }
    }

    const canRecalculate = editPermission
        // && publishedCount === 0
        && payrollData?.status !== 'completed'

    // Get the PaySlipId from sessionStorage
    const sessionPaySlipId = sessionStorage.getItem("PaySlipId");

    const steps = [
        "Initializing payroll process...",
        "Calculating salaries...",
        "Processing deductions...",
        "Generating payslips...",
        "Finalizing payroll..."
    ];

    const fetchPayrollData = async (paySlipId) => {
        try {
            const response = await fetch(PAYROLL_PROCESS, { id: paySlipId });
            if (response?.status === "success") {
                const payload = response.data || {};
                // `result` is the current page of payslips. Totals, period, and
                // status sit beside it. The first payslip has none of those.
                const {
                    result: _payslips,
                    no_of_pages: _pages,
                    total_count: _count,
                    page_no: _pageNo,
                    page_size: _pageSize,
                    ...summary
                } = payload;
                setPayrollData(summary);
                setAttentionPage(0);
                setPayrollTableTrigger((prev) => !prev);
            } else {
                console.error("Failed to fetch payroll data:", response?.message);
            }
        } catch (error) {
            console.error("Error fetching payroll data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Clear sessionStorage when component unmounts or when navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Clear sessionStorage when page is refreshed or closed
            // sessionStorage.removeItem("PaySlipId");
            sessionStorage.removeItem("hasCompletedAnimation");
        };

        const handlePopState = () => {
            // Clear sessionStorage when browser back/forward is used
            // sessionStorage.removeItem("PaySlipId");
            sessionStorage.removeItem("hasCompletedAnimation");
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [location, navigate]);

    useEffect(() => {
        // Handle new payroll creation with loading animation
        if (PaySlipId && !fromEdit && !hasCompletedAnimation) {
            // Store in sessionStorage BEFORE starting animation
            sessionStorage.setItem("PaySlipId", PaySlipId);
            sessionStorage.setItem("hasCompletedAnimation", "false");

            setRunPayrollLoading(true);
            setCurrentStep(0);

            const stepInterval = setInterval(() => {
                setCurrentStep(prev => {
                    if (prev >= steps.length - 1) {
                        clearInterval(stepInterval);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 600);

            const timeout = setTimeout(() => {
                clearInterval(stepInterval);
                setRunPayrollLoading(false);
                setCurrentStep(0);
                setHasCompletedAnimation(true);
                sessionStorage.setItem("hasCompletedAnimation", "true");

                // Fetch data after animation completes
                fetchPayrollData(PaySlipId);

                // Clear the state after animation completes
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...location.state,
                        PaySlipId: undefined
                    }
                });
            }, 3000);

            return () => {
                clearInterval(stepInterval);
                clearTimeout(timeout);
            };
        }

        // Handle editing existing payroll
        if (fromEdit && editDetails) {
            setLoading(true);
            // Fetch existing payroll data for editing
            if (editDetails.id) {
                fetchPayrollData(editDetails.id);
            } else {
                setTimeout(() => {
                    setLoading(false);
                }, 1500);
            }
        }

        // Handle case when coming back to page with sessionStorage data (including after refresh)
        if (sessionPaySlipId && !PaySlipId && !fromEdit) {
            // If we have a session PaySlipId but no animation state, fetch the data
            if (hasCompletedAnimation || sessionStorage.getItem("hasCompletedAnimation") === "true") {
                // Animation was completed previously, just fetch data
                fetchPayrollData(sessionPaySlipId);
            } else {
                // This is a fresh visit with session data but no animation completed
                // You might want to show a different state or directly fetch data
                fetchPayrollData(sessionPaySlipId);
                setHasCompletedAnimation(true);
                sessionStorage.setItem("hasCompletedAnimation", "true");
            }
        }

    }, [PaySlipId, fromEdit, editDetails, hasCompletedAnimation, sessionPaySlipId, navigate, location]);

    // handle edit the record 
    const handleEdit = (record) => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to edit payroll"); return; }
        setSelectedRow(record);
        setEditId(record?.payslip_id);
        setEditMode(true);
        setOpenDialog(true);
    }

    // dialog open close
    const handleDialogClose = () => {
        setOpenDialog(false);
        // Reset lists after a short delay to ensure dialog is closed
        setTimeout(() => {
            setCreditList([{ name: "", amount: 0 }]);
            setDebitList([{ name: "", amount: 0 }]);
            setReimburseList([{ name: "", amount: 0 }]);
            setTotalCredit(0);
            setTotalDebit(0);
            setTotalReimburse(0);
            setOvertimeAmount(0);
            setOvertimeHours(0);
        }, 300);
    }

    // Handle save changes
    const handleDialogSave = async () => {
        setEditLoading(true);
        try {
            const arrayToObject = (array) => {
                const obj = {};
                array.forEach(item => {
                    const amount = Number(item.amount);
                    if (item.name && Number.isFinite(amount) && amount !== 0) {
                        obj[item.name] = amount;
                    }
                });
                return obj;
            };

            let debitBreakup = arrayToObject(debitList);
            const fineToApply = Math.max(
                payslipShortDutyFineSnapshot(selectedRow),
                Number(liveShortDutyFine || 0),
            );
            if (fineToApply > 0) {
                debitBreakup[SHORT_DUTY_FINE_LABEL] = fineToApply;
            }
            const mergedDebitTotal = Object.values(debitBreakup).reduce(
                (sum, amount) => sum + Number(amount || 0),
                0,
            );

            const creditsBreakup = arrayToObject(creditList);
            Object.keys(creditsBreakup).forEach((key) => {
                if (isOvertimeCreditName(key)) {
                    delete creditsBreakup[key];
                }
            });
            const otAmount = Number(overtimeAmount || 0);
            if (Number.isFinite(otAmount) && otAmount > 0) {
                creditsBreakup[OVERTIME_CREDIT_LABEL] = otAmount;
            }
            const mergedCreditTotal = Object.values(creditsBreakup).reduce(
                (sum, amount) => sum + Number(amount || 0),
                0,
            );

            const totalPaidSalary = computeUpdatedFinalPay(
                selectedRow,
                mergedCreditTotal,
                totalReimburse,
                mergedDebitTotal,
            );

            const updatedFormData = {
                payslip_id: editId,
                debit: mergedDebitTotal || 0,
                debit_breakup: debitBreakup,
                credits: mergedCreditTotal || 0,
                credits_breakup: creditsBreakup,
                lop_amount: selectedRow?.lop_amount || 0,
                lop_days: selectedRow?.lop_days || 0,
                reimbursements: arrayToObject(reimburseList),
                reimbursements_total: totalReimburse || 0,
                paid_salary: totalPaidSalary || 0
            };

            // console.log(JSON.stringify(updatedFormData));

            // Call your API here
            const response = await update(PAYROLL_PAYSLIP_EDIT, updatedFormData);

            if (response?.status === "fail") {
                ToastMsg("error", response?.message || "Failed to update payroll");
                return;
            }

            // reseting the credit, debit, reimb
            setCreditList([{ name: "", amount: 0 }])
            setTotalCredit(0)
            setDebitList([{ name: "", amount: 0 }])
            setTotalDebit(0)
            setReimburseList([{ name: "", amount: 0 }])
            setTotalReimburse(0)

            setOpenDialog(false);
            ToastMsg("success", response?.message || "Payroll updated successfully.");
            fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails?.id);

        } catch (error) {
            console.error('Error updating payroll:', error);
            ToastMsg("error", "Failed to update payroll");
        } finally {
            setEditLoading(false);
        }
    };

    // Publish individual employee payslips (mark them as published)
    const handlePublishForAll = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to publish payroll"); return; }
        try {
            setLoading(true);

            const payrollId = sessionPaySlipId || PaySlipId || editDetails?.id;

            let updatedFormData = {
                payroll_id: payrollId,
                publish_all_drafts: true,
            };

            // console.log("Publishing payslips:", JSON.stringify(updatedFormData));

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false);
            if (response.status !== "fail") {
                setEverythingPublished(true);
                setLoading(false);
                ToastMsg("success", `Published ${draftCount} employee payslips`);
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
                setLoading(false);
            }
        } catch (error) {
            ToastMsg("error", error.message);
            setLoading(false);
        }
    }

    // Finalize the entire payroll run
    const handleFinalizePayroll = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to finalize payroll"); return; }
        try {
            setLoading(true);

            let updatedFormData = {
                payroll_id: sessionPaySlipId || PaySlipId || editDetails.id,
                published_date: moment().format("YYYY-MM-DD HH:mm:ss"),
                action: 'finalize_payroll' // Add action type for clarity
            };

            // console.log("Finalizing payroll:", JSON.stringify(updatedFormData));

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false);
            if (response.status !== "fail") {
                setLoading(false);
                ToastMsg("success", "Payroll finalized successfully!");
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
                setLoading(false);
            }
        } catch (error) {
            ToastMsg("error", error.message);
            setLoading(false);
        }
    }

    const fetchPendingPenalties = async (fromDate, toDate) => {
        const response = await fetch(PAYROLL_ATTENDANCE_PENALTIES, {
            from_date: fromDate,
            to_date: toDate,
            reviewed: "false",
            page_no: 1,
            page_size: 500,
        }, false);
        const pending = response?.data?.result || [];
        const total = response?.data?.total_count ?? pending.length;
        setShortDutyItems(pending);
        setPendingPenaltyTotal(total);
        return { pending, total };
    };

    const resetPenaltyEdit = () => {
        setPenaltyEditOpen(false);
        setPenaltyEditRow(null);
        setPenaltyFormData({ mode: "deduction", deduction: 0.5, fine: 0 });
    };

    const handlePenaltyAccept = async (row) => {
        if (!row?.attendance_id) return;
        setPenaltyAcceptLoadingId(row.attendance_id);
        try {
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                action: "accept_suggested",
                attendance_ids: [row.attendance_id],
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Penalty accepted");
                const { pending } = await fetchPendingPenalties(pendingPenaltyFromDate, pendingPenaltyToDate);
                if (pending.length === 0) {
                    setShortDutyDialogOpen(false);
                    setRecalculateConfirmOpen(true);
                }
            } else {
                ToastMsg("error", response?.message || "Failed to accept penalty");
            }
        } catch (error) {
            ToastMsg("error", error?.message || "Failed to accept penalty");
        } finally {
            setPenaltyAcceptLoadingId(null);
        }
    };

    const handlePenaltyAcceptAll = async () => {
        if (!pendingPenaltyFromDate || !pendingPenaltyToDate) return;
        setPenaltyBulkAcceptLoading(true);
        try {
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                action: "accept_suggested",
                from_date: pendingPenaltyFromDate,
                to_date: pendingPenaltyToDate,
            });
            if (response?.status === "success") {
                const count = response?.data?.updated ?? 0;
                ToastMsg("success", count
                    ? `Accepted ${count} pending ${count === 1 ? "penalty" : "penalties"}`
                    : (response.message || "No pending penalties in this period"));
                setShortDutyItems([]);
                setPendingPenaltyTotal(0);
                setShortDutyDialogOpen(false);
                setRecalculateConfirmOpen(true);
            } else {
                ToastMsg("error", response?.message || "Failed to accept penalties");
            }
        } catch (error) {
            ToastMsg("error", error?.message || "Failed to accept penalties");
        } finally {
            setPenaltyBulkAcceptLoading(false);
        }
    };

    const handlePenaltyOpenEdit = (row) => {
        setPenaltyEditRow(row);
        setPenaltyFormData({
            mode: row?.mode === "fine" ? "fine" : "deduction",
            deduction: Number(row?.deduction ?? 0.5),
            fine: Number(row?.fine ?? 0),
        });
        setPenaltyEditOpen(true);
    };

    const handlePenaltySave = async () => {
        if (!penaltyEditRow?.attendance_id) return;
        setPenaltySaveLoading(true);
        try {
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                attendance_id: penaltyEditRow.attendance_id,
                mode: penaltyFormData.mode,
                deduction: penaltyFormData.deduction,
                fine: penaltyFormData.fine,
                penalty_type: penaltyEditRow.penalty_type,
                is_late: penaltyEditRow.is_late,
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Penalty updated");
                resetPenaltyEdit();
                const { pending } = await fetchPendingPenalties(pendingPenaltyFromDate, pendingPenaltyToDate);
                if (pending.length === 0) {
                    setShortDutyDialogOpen(false);
                    setRecalculateConfirmOpen(true);
                }
            } else {
                ToastMsg("error", response?.message || "Failed to update penalty");
            }
        } catch (error) {
            ToastMsg("error", error?.message || "Failed to update penalty");
        } finally {
            setPenaltySaveLoading(false);
        }
    };

    const handleRecalculateClick = async () => {
        if (!editPermission) {
            ToastMsg("error", "You don't have permission to recalculate payroll");
            return;
        }
        if (!payrollData?.from_date || !payrollData?.to_date) {
            setRecalculateConfirmOpen(true);
            return;
        }
        setShortDutyCheckLoading(true);
        try {
            const fromDate = payrollData.from_date;
            const toDate = payrollData.to_date;
            setPendingPenaltyFromDate(fromDate);
            setPendingPenaltyToDate(toDate);
            setPendingPenaltyPeriodLabel(
                `${moment(fromDate).format("DD MMM YYYY")} – ${moment(toDate).format("DD MMM YYYY")}`
            );
            const { pending } = await fetchPendingPenalties(fromDate, toDate);
            if (pending.length > 0) {
                setShortDutyDialogOpen(true);
            } else {
                setRecalculateConfirmOpen(true);
            }
        } catch (error) {
            ToastMsg("error", error?.message || "Could not check attendance penalties");
        } finally {
            setShortDutyCheckLoading(false);
        }
    };

    const handleReviewShortDuty = () => {
        setShortDutyDialogOpen(false);
        navigate("/payroll/attendancepenalties", {
            state: {
                fromDate: payrollData?.from_date,
                toDate: payrollData?.to_date,
                reviewed: "false",
                fromPayrollRun: true,
                payrollId: payrollData?.id,
            },
        });
    };

    const handleRecalculateAnyway = () => {
        setShortDutyDialogOpen(false);
        setRecalculateConfirmOpen(true);
    };

    const handleRecalculatePayroll = async () => {
        if (!editPermission) {
            ToastMsg("error", "You don't have permission to recalculate payroll");
            return;
        }
        const payrollId = sessionPaySlipId || PaySlipId || editDetails?.id;
        if (!payrollId) {
            ToastMsg("error", "Payroll ID not found");
            return;
        }
        try {
            setRecalculateLoading(true);
            const response = await update(PAYROLL_RECALCULATE, { payroll_id: payrollId }, false);
            if (response.status !== "fail") {
                ToastMsg("success", response.message || "Payroll recalculated successfully");
                setEverythingPublished(false);
                fetchPayrollData(payrollId);
            } else {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to recalculate payroll");
        } finally {
            setRecalculateLoading(false);
            setRecalculateConfirmOpen(false);
        }
    };

    const handleIndividualPublish = async (record) => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to publish payroll"); return; }
        try {
            // Set loading for specific row
            setIndividualLoading(prev => ({ ...prev, [record.payslip_id]: true }));

            let updatedFormData = {
                payslip_ids: record?.payslip_id ? [record.payslip_id] : [],
            };

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false)

            if (response.status !== "fail") {
                ToastMsg("success", 'Payroll Published Successfully');
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            // Clear loading for specific row
            setIndividualLoading(prev => ({ ...prev, [record.payslip_id]: false }));
        }
    }

    const handleExportToExcel = async (type) => {
        try {
            setLoading(true);

            // Add a small delay to show loading state
            await new Promise(resolve => setTimeout(resolve, 500));

            const response = await getCSVData(PAYROLL_PROCESS, { id: sessionPaySlipId || PaySlipId || editDetails.id }, type);
            if (response) {
                if (response?.status !== "fail") {
                    ToastMsg("success", "Data exported successfully!");
                } else {
                    ToastMsg("error", response?.message || "Failed to export data");
                }
            }
        } catch (error) {
            console.error("Export error:", error);
            ToastMsg("error", error.message || "Failed to export data");
        } finally {
            setLoading(false);
        }
    }

    // Download the bank / NEFT payment advice (published payslips + bank details).
    const handleBankAdvice = async () => {
        try {
            setLoading(true);
            const response = await getCSVData(PAYROLL_BANK_ADVICE, { id: sessionPaySlipId || PaySlipId || editDetails.id }, "csv");
            if (response && response?.status === "fail") {
                ToastMsg("error", response?.message || "No published payslips to export");
            } else {
                ToastMsg("success", "Bank advice downloaded");
            }
        } catch (error) {
            console.error("Bank advice error:", error);
            ToastMsg("error", error.message || "Failed to download bank advice");
        } finally {
            setLoading(false);
        }
    }

    const handleAttendanceOpen = () => {
        setConfirmDialogOpen(true)
    }

    const handleAttendanceClose = () => {
        setConfirmDialogOpen(false)
    }

    const handleAttendanceConfirm = () => {
        navigate('/attendance/attendancelog', {
            state: {
                from: 'payroll',
                fromPayrollRun: true,
                payrollPeriod: payrollData?.payroll_month,
                payrollYear: payrollData?.payroll_year,
                payrollId: payrollData?.id,
            },
        })
    }

    const payrollColumns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "280px",
            Cell: ({ value, row }) => {
                const record = row;

                const empDetails = {
                    ...record?.employee,
                    designation_id: record?.designation_id ? {
                        id: record.designation_id,
                        designation_name: record.designation_name,
                    } : { id: 0, designation_name: "" },
                    first_name: record?.employee_name || "-",
                    id: record?.employee_code || "-",
                };

                return (
                    <div className="flex flex-col gap-1">
                        <EmployeeDetails details={empDetails} size="xs" />
                        {record?.has_adjustment_overrides && (
                            <span
                                title="One or more allowance/deduction lines were adjusted during payroll review"
                                className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border border-amber-200 dark:border-amber-700"
                            >
                                Payroll override
                            </span>
                        )}
                        {(record?.highlight_reasons || []).includes("missing_salary") && (
                            <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-100 border border-rose-300 dark:border-rose-700">
                                Missing salary
                            </span>
                        )}
                        {(record?.highlight_reasons || []).includes("future_doj") && (
                            <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-violet-200 text-violet-900 dark:bg-violet-900/50 dark:text-violet-100 border border-violet-400">
                                Future joining date
                            </span>
                        )}
                        {(record?.highlight_reasons || []).includes("missing_doj") && (
                            <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-800 dark:bg-slate-800/60 dark:text-slate-100 border border-slate-400">
                                Missing joining date
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "employee_code",
            Header: "Employee ID",
            sortable: true,
            filterable: true,
            width: "140px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "designation_name",
            Header: "Designation",
            sortable: true,
            filterable: true,
            width: "180px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "ctc",
            Header: "CTC",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-gray-900 dark:text-white">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "earnings",
            Header: "Earnings",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-green-600 dark:text-green-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "debit",
            Header: "Debits",
            sortable: true,
            width: "130px",
            Cell: ({ value, row }) => {
                const record = row.original || row;
                const debitMeta = record?.debit_breakup_meta || {};
                const hasDebitOverride = Object.values(debitMeta).some((m) => m?.overridden);
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-rose-600 dark:text-rose-400">
                            ₹{Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                        {hasDebitOverride && (
                            <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300">Override</span>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "deductions",
            Header: "Deductions",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-red-600 dark:text-red-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "lop_amount",
            Header: "LOP Amount",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-amber-600 dark:text-amber-400">
                    ₹{formatPayrollCurrency(value)}
                </span>
            ),
        },
        {
            accessor: "net_pay",
            Header: "Net Pay",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-bold text-blue-600 dark:text-blue-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        // Doc Share — only when an eMedHub integration is active for this company.
        ...(payrollData?.emedhub_connected ? [{
            accessor: "doctor_share",
            Header: "Doc Share",
            sortable: true,
            width: "160px",
            Cell: ({ value, row }) => {
                const record = row.original || row;
                const amount = Number(value || 0);
                return (
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-purple-600 dark:text-purple-400">
                            ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                        {amount > 0 && (
                            <button
                                type="button"
                                title="View doctor share breakdown"
                                onClick={() => openShareDetail(record)}
                                className="p-1 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400"
                            >
                                <Eye className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                );
            },
        }] : []),
        {
            accessor: "paid_salary",
            Header: "Paid Salary",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-semibold text-gray-900 dark:text-white">
                    ₹{displayPaidSalary(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            accessor: "overtime",
            Header: "Overtime",
            sortable: false,
            width: "150px",
            Cell: ({ value }) => (
                <OvertimeCell data={value} periodFrom={payrollData?.from_date} />
            ),
        },
        {
            accessor: "leave_encashment",
            Header: "Leave Encashment",
            sortable: true,
            width: "160px",
            Cell: ({ value }) => (
                <span className="font-medium text-teal-700 dark:text-teal-300">
                    ₹{Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            accessor: "is_publish",
            Header: "Status",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                    }`}>
                    {value ? "Published" : "Draft"}
                </span>
            ),
        },
        {
            accessor: "actions",
            Header: "Actions",
            width: "120px",
            fixed: true,
            align: 'right',
            Cell: ({ row }) => {
                const record = row;
                const isRowLoading = individualLoading[record.payslip_id];

                return (
                    <div className={cn(`flex gap-2`, isRowLoading ? ' justify-end' : 'items-center')}>
                        {!record.is_publish && (
                            <>
                                {
                                    isRowLoading ? (
                                        <div className="dark:bg-gray-900 flex items-end justify-end rounded-md">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                                        </div>
                                    ) : editPermission ? (
                                        <>
                                            <button
                                                onClick={() => handleEdit(record)}
                                                className="p-1.5 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleIndividualPublish(record)}
                                                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                                title="Publish"
                                            >
                                                <Send className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : null
                                }
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    const shortDutyColumns = useMemo(() => ([
        {
            accessor: "employee_name",
            Header: "Employee",
            filterable: true,
            Cell: ({ row }) => (
                <div>
                    <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
                </div>
            ),
        },
        {
            accessor: "department_name",
            Header: "Department",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "date",
            Header: "Date",
            Cell: ({ value }) => (value ? moment(value).format("DD MMM YYYY") : "-"),
        },
        {
            accessor: "penalty_type",
            Header: "Type",
            Cell: ({ value }) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "penalty",
            Header: "Penalty",
            Cell: ({ value }) => (
                <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                    value === "Fine"
                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                )}>
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "value",
            Header: "Value",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "review_status",
            Header: "Status",
            Cell: ({ value }) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "reason",
            Header: "Reason",
            Cell: ({ value }) => value || "-",
        },
        ...(editPermission ? [{
            accessor: "action",
            Header: "Actions",
            sortable: false,
            fixed: true,
            align: "right",
            Cell: ({ row }) => {
                const isPending = row?.review_status !== "Reviewed";
                const isAccepting = penaltyAcceptLoadingId === row?.attendance_id;
                return (
                    <div className="flex items-center justify-end gap-1">
                        {isPending && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                disabled={isAccepting || penaltyBulkAcceptLoading}
                                onClick={() => handlePenaltyAccept(row)}
                                title="Accept suggested penalty"
                            >
                                {isAccepting ? (
                                    <span className="h-4 w-4 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={() => handlePenaltyOpenEdit(row)}
                            title={isPending ? "Change penalty (fine or half-day leave)" : "Edit penalty"}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    </div>
                );
            },
        }] : []),
    ]), [editPermission, penaltyAcceptLoadingId, penaltyBulkAcceptLoading]);

    // Show loading for new payroll creation
    if (runPayrollLoading && PaySlipId && !fromEdit) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="max-w-md w-full mx-4 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
                    {/* Animated Spinner */}
                    <div className="flex justify-center mb-6">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary"></div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span>Step {currentStep + 1} of {steps.length}</span>
                            <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-sidebar-primary h-2 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Current Step Text */}
                    <div className="text-center">
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                            Creating New Payroll
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-1">
                            {steps[currentStep]}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                            This will only take a moment...
                        </p>
                    </div>

                    {/* Step Indicators */}
                    <div className="flex justify-center mt-6 space-x-1">
                        {steps.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-colors duration-300 ${index <= currentStep
                                    ? 'bg-sidebar-primary'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Show loading for editing existing payroll or fetching data
    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300 mb-2">
                    {fromEdit ? "Loading Payroll Data" : "Fetching Payroll Details"}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-center">
                    {fromEdit
                        ? "Please wait while we load the existing payroll data..."
                        : "Please wait while we fetch the payroll details..."
                    }
                </p>
                <div className="mt-6 w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-sidebar-primary h-2 rounded-full animate-pulse"></div>
                </div>
            </div>
        );
    }

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <div className="flex flex-wrap items-start sm:items-center gap-4 mb-4">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => {
                        sessionStorage.removeItem("PaySlipId");
                        sessionStorage.removeItem("hasCompletedAnimation");
                        navigate("/payroll/payrollprocess");
                    }}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Payroll Process" : "Payroll Process Details"}
                    </h1>
                        {payrollData?.status && (
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide",
                                    getPayrollStatusChipClass(payrollData.status)
                                )}
                            >
                                <Circle className="h-1.5 w-1.5 fill-current shrink-0" />
                                {getPayrollStatusLabel(payrollData.status)}
                            </span>
                        )}
                    </div>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-2">
                        {fromEdit ? "Continue existing payroll data processing" : "Manage and process employee payroll calculations"}
                    </p>
                </div>

                {/* Payroll Month & Header Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                    <div className="text-right sm:text-right">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Payroll Month</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                            {payrollMonthLabel}
                        </p>
                    </div>
                    {canRecalculate && (
                        <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-amber-400 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleRecalculateClick}
                            disabled={recalculateLoading || shortDutyCheckLoading}
                            title="Refresh payslips with latest allowances, deductions, and attendance"
                        >
                            <RefreshCw className={`h-4 w-4 shrink-0 ${(recalculateLoading || shortDutyCheckLoading) ? 'animate-spin' : ''}`} />
                            Recalculate
                        </button>
                    )}
                </div>
            </div>

            <PayrollProfileLockBanner className="mb-4" />

            {totalEmployees === 0 && (
                <div className="mb-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
                    <p className="font-medium">No employees were included in this payroll run.</p>
                    <p className="mt-1 text-amber-800 dark:text-amber-200">
                        Payroll only picks people in the departments selected at create
                        {(payrollData?.department_names || []).length
                            ? ` (this run: ${(payrollData.department_names || []).join(", ")})`
                            : ""}
                        , who are not on payroll hold, and who have a salary structure plus attendance between{" "}
                        {payrollData?.from_date ? moment(payrollData.from_date).format("DD MMM YYYY") : "the start"} and{" "}
                        {payrollData?.to_date ? moment(payrollData.to_date).format("DD MMM YYYY") : "the end"}.
                        Attendance Log can still show people in other departments. Recalculate uses the same
                        departments — delete this draft and generate again with the right departments (or All).
                    </p>
                </div>
            )}

            {/* Payroll Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Total Paid Salary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Total Salary</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_paid_salary?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                            <IndianRupee className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Net payable amount</p>
                    </div>
                </div>

                {/* Total Deductions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Total Deductions</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_deductions?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-rose-100 dark:bg-rose-900/30 rounded-xl flex items-center justify-center">
                            <Minus className="h-6 w-6 text-rose-600 dark:text-rose-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total deductions applied</p>
                    </div>
                </div>

                {/* Total Employees */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Employees</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                {payrollData?.total_employee || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                            <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total employees processed</p>
                    </div>
                </div>

                {/* Payroll Period */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Payroll Period</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                {payrollData?.from_date ? moment(payrollData.from_date).format('DD MMM') : '-'} - {payrollData?.to_date ? moment(payrollData.to_date).format('DD MMM') : '-'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center">
                            <Calendar className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <Clock className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">{payrollData?.total_days || 0} working days</p>
                    </div>
                </div>
            </div>

            {/* Additional Details Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

                {/* Financial Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <PieChart className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white">Financial Summary</h3>
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Gross Salary</span>
                            </div>
                            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                ₹{(Number(payrollData?.total_paid_salary) + Number(payrollData?.total_deductions) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <Minus className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Deductions</span>
                            </div>
                            <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                                ₹{payrollData?.total_deductions?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <div className="flex items-center gap-2">
                                <IndianRupee className="h-4 w-4 text-gray-400" />
                                <span className="text-md font-semibold text-gray-900 dark:text-white">Net Payable</span>
                            </div>
                            <span className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_paid_salary?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Zap className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
                    </div>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* Show completed message if payroll is completed */}
                            {payrollData?.status === 'completed' ? (
                                <div className="col-span-full">
                                    <div className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 px-4 rounded-lg text-sm font-semibold">
                                        <CheckCircle className="h-4 w-4" />
                                        Payroll Completed ✓
                                    </div>
                                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                                        This payroll has been finalized and cannot be modified
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Publish Individual Employee Payslips */}
                                    {editPermission && !everythingPublished && draftCount > 0 && (
                                        <Button
                                            variant="brand"
                                            className="w-full h-11 gap-2 font-semibold"
                                            onClick={handlePublishForAll}
                                            disabled={payrollData?.status === 'completed'}
                                        >
                                            <Send className="h-4 w-4" />
                                            Publish All Drafts ({draftCount})
                                        </Button>
                                    )}

                                    {editPermission && (
                                        <Button
                                            variant="brand"
                                            className={cn(
                                                "w-full h-11 gap-2 font-semibold",
                                                (!everythingPublished && draftCount > 0) ? "sm:col-span-1" : "sm:col-span-2",
                                            )}
                                            onClick={handleFinalizePayroll}
                                            disabled={payrollData?.status === 'completed' || publishedCount === 0}
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            Finalize Payroll
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Secondary Actions */}
                        <Button
                            variant="secondary"
                            className="w-full h-11 gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold"
                            onClick={() => handleExportToExcel("csv")}
                        >
                            <Download className="h-4 w-4" />
                            Export to Excel
                        </Button>
                        {editPermission && (
                            <Button
                                className="w-full h-11 gap-2 bg-sidebar-primary hover:bg-sidebar-primary/90 text-white font-semibold"
                                onClick={handleBankAdvice}
                                title="Download the bank / NEFT payment file for published payslips"
                            >
                                <FileText className="h-4 w-4" />
                                Bank Advice
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            className="w-full h-11 gap-2 font-semibold"
                            onClick={() => handleAttendanceOpen()}
                        >
                            <Calendar className="h-4 w-4" />
                            View Attendance
                        </Button>
                    </div>
                </div>
            </div>

            {(payrollData?.attention_employees || []).length > 0 && (
                <div className="mx-4 mt-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4">
                    <h4 className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                        Employees who need attention ({payrollData.attention_employees.length})
                    </h4>
                    <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
                        Missing salary is marked in rose. A future date of joining is marked in violet. Missing joining date is marked in slate. These employees are not in the payroll list below.
                    </p>
                    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                        {payrollData.attention_employees.slice(attentionPage * 8, attentionPage * 8 + 8).map((employee) => {
                            const missingSalary = (employee.reasons || []).includes("missing_salary");
                            const futureDoj = (employee.reasons || []).includes("future_doj");
                            const missingDoj = (employee.reasons || []).includes("missing_doj");
                            return (
                                <li
                                    key={employee.employee_id}
                                    className={cn(
                                        "rounded-lg border px-3 py-2",
                                        missingSalary
                                            ? "border-rose-300 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-700"
                                            : futureDoj
                                            ? "border-violet-300 bg-violet-50 dark:bg-violet-950/40 dark:border-violet-700"
                                            : missingDoj
                                            ? "border-slate-300 bg-slate-50 dark:bg-slate-900 dark:border-slate-600"
                                            : "border-amber-300 bg-white dark:bg-gray-900 dark:border-amber-700"
                                    )}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{employee.employee_name}</span>
                                        <span className="text-xs text-gray-500">{employee.employee_code}</span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {missingSalary && (
                                            <span className="rounded-full bg-rose-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-900">Missing salary</span>
                                        )}
                                        {futureDoj && (
                                            <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-900">
                                                Joining {employee.date_of_joining ? dayjs(employee.date_of_joining).format("DD MMM YYYY") : "in the future"}
                                            </span>
                                        )}
                                        {missingDoj && (
                                            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-800">Missing joining date</span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {payrollData.attention_employees.length > 8 && (
                        <div className="mt-3 flex items-center justify-between text-xs text-amber-900/80 dark:text-amber-200/80">
                            <span>
                                Showing {attentionPage * 8 + 1}–
                                {Math.min((attentionPage + 1) * 8, payrollData.attention_employees.length)} of {payrollData.attention_employees.length}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={attentionPage === 0}
                                    onClick={() => setAttentionPage((page) => Math.max(0, page - 1))}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={(attentionPage + 1) * 8 >= payrollData.attention_employees.length}
                                    onClick={() => setAttentionPage((page) => page + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Employee List Table Section */}
            <div className=" dark:bg-gray-800 rounded-xl dark:border-gray-700">
                <div className="py-6 px-4 border-b rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Left Section - Title and Description */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <Users className="h-6 w-6 text-sidebar-primary" />
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Employee Payroll List
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Manage and review individual employee salary calculations, deductions, and net pay
                            </p>
                        </div>

                        {/* Right Section - Status Summary */}
                        <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
                            {/* Total Employees Card */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                                        Total
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                    {totalEmployees || 0}
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                    Employees
                                </p>
                            </div>

                            {/* Published Employees Card */}
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
                                        Published
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                                    {publishedCount || 0}
                                </p>
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    {totalEmployees > 0 ? Math.round((publishedCount / totalEmployees) * 100) : 0}% Complete
                                </p>
                            </div>

                            {/* Draft Employees Card */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                    <span className="text-sm font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                                        Draft
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                                    {draftCount || 0}
                                </p>
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    Pending Review
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    {totalEmployees > 0 && (
                        <div className="mt-6">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                    Publication Progress
                                </span>
                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                    {publishedCount} of {totalEmployees} completed
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                <div
                                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                                    style={{
                                        width: `${totalEmployees > 0 ? (publishedCount / totalEmployees) * 100 : 0}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Your employee table component will go here */}
                <div className="pt-2">
                    <Reactable
                        columns={payrollColumns}
                        data={RunPayrollProcessData}
                        setData={setRunPayrollProcessData}
                        setTotalCount={setPayrollTableTotal}
                        url={PAYROLL_PROCESS}
                        filter={{ id: sessionPaySlipId || PaySlipId || editDetails?.id }}
                        serverTrigger={payrollTableTrigger}
                        onRowHighlightKey="is_publish"
                        serverSide={true}
                        rowSize="sm"
                        isDraftRequired={false}
                        isCSVRequired={false}
                        isPDFRequired={false}
                        isFilterRequired={false}
                    />
                </div>

                <DialogModal
                    open={openDialog}
                    onClose={handleDialogClose}
                    loading={editLoading}
                    onSave={handleDialogSave}
                    title="Edit Employee Payroll"
                    width="max-w-5xl"
                >
                    <div className="space-y-4 max-h-[calc(100vh-210px)] overflow-y-auto pr-1">
                        {/* Header */}
                        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                            <div className="min-w-0">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{selectedRow?.employee_name}</h2>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                    <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                        {selectedRow?.employee_code}
                                    </span>
                                    <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                        {selectedRow?.designation_name}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-stretch gap-2.5">
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-right">
                                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Current Net Pay</div>
                                    <div className="text-base font-semibold text-gray-700 dark:text-gray-200">
                                        ₹{Number(selectedRow?.net_pay || 0).toLocaleString('en-IN')}
                                    </div>
                                </div>
                                <div className="rounded-lg border border-sidebar/30 bg-sidebar/10 px-3 py-1.5 text-right">
                                    <div className="text-[11px] uppercase tracking-wide text-sidebar-primary/80">Updated Final Pay</div>
                                    <div className="text-base font-bold text-sidebar-primary">
                                        ₹{computeUpdatedFinalPay(selectedRow, effectiveTotalCredit, totalReimburse, totalDebit).toLocaleString('en-IN')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Grid Layout */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            {/* Left Column - Fixed Earnings & Deductions */}
                            <div className="xl:col-span-1 space-y-4">
                                {/* Earnings */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                                        <div className="flex flex-col gap-0.5 min-w-0">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Earnings</span>
                                            {selectedRow?.earnings_breakup?._payroll_prorate ? (
                                                <span className="text-[11px] text-blue-600 dark:text-blue-400">
                                                    Pro-rated for {selectedRow.earnings_breakup._payroll_prorate.eligible_days} of {selectedRow.earnings_breakup._payroll_prorate.total_days} days
                                                </span>
                                            ) : null}
                                        </div>
                                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                            ₹{displayEarningsTotal(selectedRow).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                        {selectedRow?.earnings_breakup && Object.entries(selectedRow.earnings_breakup).map(([key, earning]) => (
                                            key.startsWith('_') ? null : earning?.name && earning.monthly_amount !== null && (
                                                <div key={key} className="flex items-center justify-between px-4 py-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{earning.name}</span>
                                                        {earning.percentage ? (
                                                            <span className="flex-shrink-0 text-[11px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded">{earning.percentage}%</span>
                                                        ) : null}
                                                    </div>
                                                    <span className="flex-shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        ₹{Number(earning.monthly_amount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>

                                {/* Deductions */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Deductions</span>
                                        <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                                            ₹{Number(selectedRow?.deductions || 0).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    {visibleDeductions(selectedRow?.deductions_breakup).length > 0 ? (
                                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {visibleDeductions(selectedRow?.deductions_breakup).map((deduction) => (
                                                <div key={deduction.label} className="flex items-center justify-between px-4 py-2">
                                                    <div className="min-w-0">
                                                        <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{deduction.label}</div>
                                                        <div className="text-[11px] text-gray-400">Employer ₹{Number(deduction.employer_share_monthly_amount || 0).toLocaleString('en-IN')}</div>
                                                    </div>
                                                    <span className="flex-shrink-0 text-sm font-medium text-rose-600 dark:text-rose-400">
                                                        ₹{Number(deduction.employee_share_monthly_amount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-gray-400">No deductions</div>
                                    )}
                                </div>

                                {/* Loss of Pay */}
                                <div className="rounded-xl border border-amber-200/70 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10 px-4 py-3">
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Loss of Pay</span>
                                        <div className="flex items-center gap-5">
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">{selectedRow?.lop_days || 0}</div>
                                                <div className="text-[11px] text-amber-600/80 dark:text-amber-400/70">Days</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">₹{formatPayrollCurrency(selectedRow?.lop_amount)}</div>
                                                <div className="text-[11px] text-amber-600/80 dark:text-amber-400/70">Amount</div>
                                            </div>
                                        </div>
                                    </div>
                                    {Number(selectedRow?.short_duty_days || 0) > 0 && (
                                        <div className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-800/50 flex items-center justify-between text-[11px] text-amber-700/90 dark:text-amber-300/80">
                                            <span>of which short-duty deduction (missing punch)</span>
                                            <span className="font-semibold">
                                                {Number(selectedRow.short_duty_days)} {Number(selectedRow.short_duty_days) === 1 ? "day" : "days"} · ₹{Number(selectedRow?.short_duty_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    )}
                                    {Number(effectiveShortDutyFine || 0) > 0 && (
                                        <div className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-800/50 flex items-center justify-between text-[11px] text-rose-600/90 dark:text-rose-400/80">
                                            <span>short-duty fine (included in Debits)</span>
                                            <span className="font-semibold">
                                                {Number(effectiveShortDutyFineDays || 0)} {Number(effectiveShortDutyFineDays || 0) === 1 ? "day" : "days"} · ₹{formatPayrollCurrency(effectiveShortDutyFine)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column - Focus on Adjustments */}
                            <div className="xl:col-span-2 space-y-4">
                                {shortDutyFineStale && (
                                    <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
                                        Attendance has a reviewed short-duty fine of ₹{formatPayrollCurrency(liveShortDutyFine)} that is not yet on this payslip snapshot.
                                        It is previewed in Debits below — save or use <strong>Recalculate</strong> to apply it to payroll.
                                    </div>
                                )}
                                {canManageAdvanceEmi && (selectedRow?.advance_emi_items || []).length > 0 ? (
                                    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                                        <div className="flex items-center justify-between gap-2 mb-3">
                                            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Advance Salary EMI</h3>
                                            <span className="text-[11px] text-blue-700 dark:text-blue-300">Skip before finalize · amount carries to selected month</span>
                                        </div>
                                        <div className="space-y-3">
                                            {(selectedRow.advance_emi_items || []).map((item) => {
                                                const isSkipped = item.status === "skipped";
                                                const isLoading = advanceEmiSkipLoading[item.advance_salary_id];
                                                return (
                                                    <div
                                                        key={item.advance_salary_id}
                                                        className="rounded-lg border border-blue-200/80 dark:border-blue-800/80 bg-white dark:bg-gray-900 p-3"
                                                    >
                                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                                            <div className="space-y-1 text-xs">
                                                                <p className="font-semibold text-gray-900 dark:text-gray-100">
                                                                    EMI {item.installment_number ? `#${item.installment_number}` : "Carry-forward"} · ₹{Number(item.emi_amount || 0).toLocaleString("en-IN")}
                                                                </p>
                                                                <p className="text-gray-600 dark:text-gray-400">
                                                                    Approved ₹{Number(item.approved_amount || 0).toLocaleString("en-IN")} · {item.installments_paid}/{item.emi_period} paid · ₹{Number(item.outstanding_balance || 0).toLocaleString("en-IN")} outstanding
                                                                </p>
                                                                {item.carryforward_due && !item.scheduled_due ? (
                                                                    <p className="text-blue-700 dark:text-blue-300">Carried installment due this month</p>
                                                                ) : null}
                                                            </div>
                                                            <span className={cn(
                                                                "inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide",
                                                                isSkipped
                                                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                                                    : item.status === "due" || item.status === "carryforward_due"
                                                                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                                                                        : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
                                                            )}>
                                                                {isSkipped ? "Skipped" : item.status === "carryforward_due" ? "Carried Due" : "Due"}
                                                            </span>
                                                        </div>

                                                        {isSkipped ? (
                                                            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                                                                <div className="text-gray-600 dark:text-gray-400">
                                                                    Carries to <strong>{item.carry_to_month ? dayjs(item.carry_to_month).format("MMM YYYY") : "-"}</strong>
                                                                    {item.skip_reason ? ` · ${item.skip_reason}` : ""}
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    disabled={isLoading}
                                                                    onClick={() => handleAdvanceEmiRestore(item)}
                                                                >
                                                                    Restore EMI
                                                                </Button>
                                                            </div>
                                                        ) : item.scheduled_due ? (
                                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                                                <div className="md:col-span-1">
                                                                    <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">Carry to month</label>
                                                                    <DatePicker
                                                                        picker="month"
                                                                        className="w-full"
                                                                        value={advanceEmiCarryMonth[item.advance_salary_id]
                                                                            ? dayjs(`${advanceEmiCarryMonth[item.advance_salary_id]}-01`)
                                                                            : null}
                                                                        onChange={(date) => setAdvanceEmiCarryMonth((prev) => ({
                                                                            ...prev,
                                                                            [item.advance_salary_id]: date ? date.format("YYYY-MM") : "",
                                                                        }))}
                                                                        disabledDate={(current) => (
                                                                            !!payrollMonthBase
                                                                            && !!current
                                                                            && current.endOf("month").valueOf() <= payrollMonthBase.endOf("month").valueOf()
                                                                        )}
                                                                        placeholder="Select month"
                                                                    />
                                                                </div>
                                                                <div className="md:col-span-1">
                                                                    <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (optional)</label>
                                                                    <Input
                                                                        value={advanceEmiSkipReason[item.advance_salary_id] || ""}
                                                                        onChange={(e) => setAdvanceEmiSkipReason((prev) => ({
                                                                            ...prev,
                                                                            [item.advance_salary_id]: e.target.value,
                                                                        }))}
                                                                        placeholder="Why skip this EMI?"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-200"
                                                                        disabled={isLoading}
                                                                        onClick={() => handleAdvanceEmiSkip(item)}
                                                                    >
                                                                        Skip this month
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}

                                {/* Overtime */}
                                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/40 dark:bg-emerald-900/10 p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Overtime</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                Overtime Hours
                                            </label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={overtimeHours || ""}
                                                onChange={(e) => setOvertimeHours(Math.max(0, Number(e.target.value || 0)))}
                                                placeholder="0"
                                            />
                                            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                                Hours from approved overtime or attendance. Edit if the payroll figure needs correction.
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                Overtime Amount (₹)
                                            </label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={overtimeAmount || ""}
                                                onChange={(e) => setOvertimeAmount(Math.max(0, Number(e.target.value || 0)))}
                                                placeholder="0.00"
                                            />
                                            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                                Saved as an Overtime credit on the payslip.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Adjustments */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Adjustments</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
                                        <AdjustmentGroup
                                            title="Credits"
                                            accent="emerald"
                                            items={creditList}
                                            total={totalCredit}
                                            namePlaceholder="Credit name"
                                            onAdd={() => handleListAdd(creditList, setCreditList)}
                                            onChange={(i, f, v) => handleListChange(creditList, setCreditList, i, f, v)}
                                            onRemove={(i) => handleListRemove(creditList, setCreditList, i)}
                                        />
                                        <AdjustmentGroup
                                            title="Debits"
                                            accent="rose"
                                            items={debitList}
                                            total={totalDebit}
                                            namePlaceholder="Debit name"
                                            onAdd={() => handleListAdd(debitList, setDebitList)}
                                            onChange={(i, f, v) => handleListChange(debitList, setDebitList, i, f, v)}
                                            onRemove={(i) => handleListRemove(debitList, setDebitList, i)}
                                            readOnlyPredicate={(item) => Boolean(item?.isShortDutyFine)}
                                        />
                                        <AdjustmentGroup
                                            title="Reimbursements"
                                            accent="indigo"
                                            items={reimburseList}
                                            total={totalReimburse}
                                            namePlaceholder="Reimbursement name"
                                            onAdd={() => handleListAdd(reimburseList, setReimburseList)}
                                            onChange={(i, f, v) => handleListChange(reimburseList, setReimburseList, i, f, v)}
                                            onRemove={(i) => handleListRemove(reimburseList, setReimburseList, i)}
                                        />
                                    </div>
                                </div>

                                {/* Salary Summary strip */}
                                <div className="rounded-xl border border-sidebar/30 bg-sidebar/5 dark:bg-sidebar/10 px-4 py-3">
                                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                                            {[
                                                { label: "Net Pay", value: selectedRow?.net_pay, op: "", color: "text-gray-800 dark:text-gray-200" },
                                                { label: "LOP", value: selectedRow?.lop_amount, op: "−", color: "text-amber-600 dark:text-amber-400" },
                                                { label: "Credits", value: effectiveTotalCredit, op: "+", color: "text-emerald-600 dark:text-emerald-400" },
                                                { label: "Reimburse", value: totalReimburse, op: "+", color: "text-indigo-600 dark:text-indigo-400" },
                                                { label: "Debits", value: totalDebit, op: "−", color: "text-orange-600 dark:text-orange-400" },
                                                { label: "TDS", value: getTdsAmount(selectedRow), op: "−", color: "text-rose-600 dark:text-rose-400" },
                                            ].map((t, i) => (
                                                <div key={t.label} className="flex items-center gap-2.5">
                                                    {i > 0 && <span className="text-gray-400 text-sm font-medium">{t.op}</span>}
                                                    <div className="text-center">
                                                        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</div>
                                                        <div className={cn("text-sm font-semibold", t.color)}>₹{Number(t.value || 0).toLocaleString('en-IN')}</div>
                                                    </div>
                                                </div>
                                            ))}
                                            <span className="text-gray-400 text-base font-medium">=</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Final Net Payable</div>
                                            <div className="text-2xl font-bold text-sidebar-primary">
                                                ₹{computeUpdatedFinalPay(selectedRow, effectiveTotalCredit, totalReimburse, totalDebit).toLocaleString('en-IN')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogModal>

                <ConfirmPopupModal
                    open={recalculateConfirmOpen}
                    onClose={() => {
                        if (!recalculateLoading) setRecalculateConfirmOpen(false)
                    }}
                    onConfirm={handleRecalculatePayroll}
                    title="Recalculate Payroll"
                    description="This will rebuild unpublished payslips using the latest allowances, deductions, and attendance for this period. Published payslips will not be changed. Manual edits on unpublished payslips will be lost. Skipped advance EMI decisions are kept. Continue?"
                    type="warning"
                    confirmLabel="Recalculate"
                    cancelLabel="Cancel"
                    loading={recalculateLoading}
                    loadingLabel="Recalculating..."
                    destructive={false}
                >
                </ConfirmPopupModal>

                <DialogModal
                    open={shortDutyDialogOpen}
                    onClose={() => {
                        setShortDutyDialogOpen(false);
                        setShortDutyItems([]);
                        setPendingPenaltyTotal(0);
                        resetPenaltyEdit();
                    }}
                    title="Pending Attendance Penalties"
                    width="max-w-5xl"
                    showActions={false}
                >
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {pendingPenaltyTotal} pending penalty row{pendingPenaltyTotal === 1 ? "" : "s"} for payroll period{" "}
                        <span className="font-medium text-gray-800 dark:text-gray-200">{pendingPenaltyPeriodLabel}</span>.
                        Accept or edit each row below, or continue anyway (unreviewed rows are excluded from payroll).
                    </p>
                    {editPermission && pendingPenaltyTotal > 0 && (
                        <div className="flex justify-end mb-3">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={penaltyBulkAcceptLoading}
                                onClick={handlePenaltyAcceptAll}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                            >
                                {penaltyBulkAcceptLoading ? (
                                    <span className="h-4 w-4 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                                Accept all pending
                            </Button>
                        </div>
                    )}
                    <Reactable
                        pagename="pending-attendance-penalties-dialog"
                        columns={shortDutyColumns}
                        data={shortDutyItems}
                        setData={setShortDutyItems}
                        serverSide={false}
                        disableStateCache={true}
                        tableHeight="320px"
                        rowSize="sm"
                        isCSVRequired={false}
                        isPDFRequired={false}
                        isFilterRequired={false}
                    />
                    <div className="flex flex-wrap justify-end gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShortDutyDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleReviewShortDuty}
                        >
                            Review Penalties
                        </Button>
                        <Button
                            type="button"
                            onClick={handleRecalculateAnyway}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            Recalculate Anyway
                        </Button>
                    </div>
                </DialogModal>

                <DialogModal
                    open={penaltyEditOpen}
                    onClose={resetPenaltyEdit}
                    onSave={handlePenaltySave}
                    title="Override Attendance Penalty"
                    closeLabel="Cancel"
                    saveLabel="Confirm"
                    editMode={true}
                    loading={penaltySaveLoading}
                    width="440px"
                >
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {penaltyEditRow?.employee_name || "-"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {penaltyEditRow?.date || "-"} · {penaltyEditRow?.penalty_type || "-"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Choose fine (flat amount) or deduction (half-day / full-day leave), then confirm.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Penalty Mode
                            </label>
                            <Select
                                size="large"
                                className="w-full"
                                value={penaltyFormData.mode}
                                onChange={(value) => setPenaltyFormData((prev) => ({ ...prev, mode: value }))}
                            >
                                <Option value="deduction">Deduction (Loss of Pay)</Option>
                                <Option value="fine">Fine (Flat Amount)</Option>
                            </Select>
                        </div>

                        {penaltyFormData.mode === "fine" ? (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                                    <IndianRupee className="h-3 w-3" /> Fine Amount
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={penaltyFormData.fine}
                                    onChange={(e) => setPenaltyFormData((prev) => ({
                                        ...prev,
                                        fine: Number(e.target.value),
                                    }))}
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Day Fraction (0 = waive, 0.5 = half day, 1 = full day)
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={1}
                                    step={0.25}
                                    value={penaltyFormData.deduction}
                                    onChange={(e) => setPenaltyFormData((prev) => ({
                                        ...prev,
                                        deduction: Number(e.target.value),
                                    }))}
                                />
                            </div>
                        )}
                    </div>
                </DialogModal>

                <ConfirmPopupModal
                    open={confirmDialogOpen}
                    onClose={handleAttendanceClose}
                    onConfirm={handleAttendanceConfirm}
                    title="Navigate to Attendance"
                    description="You are about to navigate and modify attendance for the selected month. Please note that any changes will require re-running the payroll calculation."
                    type="warning"
                    confirmLabel="Continue"
                    cancelLabel="Cancel"
                    destructive={false}
                >
                </ConfirmPopupModal>
            </div>

            {/* eMedHub doctor-share breakdown modal */}
            {shareModalOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setShareModalOpen(false)}
                >
                    <div
                        className="w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 grid place-items-center">
                                    <Stethoscope size={20} />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">eMedHub Doctor Share</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {shareEmployee?.employee_name || "Employee"}
                                        {payrollData?.from_date ? ` · ${payrollData.from_date} to ${payrollData.to_date}` : ""}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShareModalOpen(false)}
                                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Cards */}
                        {!shareLoading && shareDetail && (
                            <div className="grid grid-cols-2 gap-3 p-5 pb-0">
                                <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Amount</div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        ₹{Number(shareDetail.Total_amount_card || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </div>
                                </div>
                                <div className="rounded-xl border border-purple-100 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/20 px-4 py-3">
                                    <div className="text-xs text-purple-700 dark:text-purple-300">Total Doctor Share</div>
                                    <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
                                        ₹{Number(shareDetail.Doctor_share_card || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <div className="p-5 overflow-auto">
                            {shareLoading ? (
                                <div className="py-12 flex justify-center">
                                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-500" />
                                </div>
                            ) : !shareDetail || !(shareDetail.data || []).length ? (
                                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No sharable services found for this period.
                                </p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/70 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-700">
                                                <th className="py-2 px-3">Module</th>
                                                <th className="py-2 px-3">Service</th>
                                                <th className="py-2 px-3">Service Head</th>
                                                <th className="py-2 px-3 text-right">Count</th>
                                                <th className="py-2 px-3 text-right">Total Amount</th>
                                                <th className="py-2 px-3 text-right">Doctor Share</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(shareDetail.data || []).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-50 dark:border-gray-800">
                                                    <td className="py-2 px-3">
                                                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                            {r.module_type || "—"}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-900 dark:text-gray-100">{r.service_name || "—"}</td>
                                                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{r.service_head || "—"}</td>
                                                    <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{r.count}</td>
                                                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">
                                                        ₹{Number(r.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="py-2 px-3 text-right font-medium text-purple-600 dark:text-purple-400">
                                                        ₹{Number(r.doctor_share || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default RunPayrollProcess;